import { keys, has, isArray, uniqueId, get } from "lodash";

const hexColors = ["#1AADFF", "#00E839", "#FF5D07", "#00DAFF", "#F2E205"];

const konvaLineConfig = (zone, options) => {
  return {
    points: zone.vertices.map((point) => [point.ui.x, point.ui.y]).flat(),
    fill: zone.color,
    stroke: "black",
    strokeWidth: 1,
    closed: true,
    lineJoin: "round",
    opacity: 0.8,
    draggable: false,
  };
};

const konvaCirclesConfig = (zone, options) => {
  return {
    fill: zone.color,
    stroke: "black",
    strokeWidth: 1,
    radius: 6,
    opacity: 1,
    draggable: !!options.draggable,
  };
};

const konvaGroupConfig = (options) => {
  return {
    draggable: !!options.draggable,
  };
};

const buildZonesForUI = (settings, options) => {
  const zones = get(settings, "aerial", []).map((zone) => {
    return {
      id: zone.id,
      name: zone.name,
      enabled: zone.state == "enabled",
      vertices: zone.vertices.map((point) => {
        return { x: point.x, y: point.y };
      }),
      properties: {
        detection_types: (zone.properties || {})["detection_types"] || [],
      },
    };
  });

  zones.toUpdate = [];
  zones.toDelete = [];

  // remove "invalid" zones that come from DR
  if (zones.length) {
    const invalidZones = zones.filter(
      (zone) =>
        !zone.enabled &&
        !zone.vertices.find((point) => point.x != 0 || point.y != 0)
    );
    invalidZones.forEach((invalidZone) =>
      zones.splice(
        zones.indexOf(zones.find((zone) => zone.id === invalidZone.id)),
        1
      )
    );
  }

  zones.forEach((zone, zoneIdx) => {
    zone.vertices.forEach((point) => {
      point.zone = zone;
      point.uniqueId = uniqueId("point-");
      point.ui = convertDataPointToScreenPoint(point, options);
    });

    zone.uniqueId = uniqueId("zone-");
    zone.color = hexColors[zoneIdx % hexColors.length];
    zone.drawn =
      zone.vertices.filter((point) => point.x != 0 && point.y != 0).length > 0;
    zone.selected = false;
    zone.config = {
      group: konvaGroupConfig(options),
      line: konvaLineConfig(zone, options),
      circles: konvaCirclesConfig(zone, options),
    };
  });

  const drawnZone = zones.find((zone) => zone.drawn);
  if (drawnZone) drawnZone.selected = true;

  zones.options = options;

  return zones;
};

const convertDataPointToScreenPoint = (point, options) => {
  return {
    x: ((10 + point.x) / 20) * options.width,
    y: (1 - point.y / 10) * options.height,
  };
};

const buildPayloadForSave = (zones, doorbotId) => {
  const mapZone = (zones, includeId) =>
    zones.map((zone) => {
      const mapped = {
        name: zone.name,
        state: zone.enabled ? "enabled" : "disabled",
        vertices: zone.vertices.map((point) => {
          return {
            x: Number(point.x.toFixed(4)),
            y: Number(point.y.toFixed(4)),
          };
        }),
        properties: zone.properties,
      };
      if (includeId) {
        mapped["id"] = zone.id;
      }
      return mapped;
    });

  const mapId = (zones) =>
    zones.map((zone) => {
      return { id: zone.id };
    });

  const toCreate = zones.filter(
    (zone) =>
      !zone.id &&
      zone.vertices.filter((point) => point.x != 0 || point.y != 0).length > 0
  );

  const toDelete = zones.toDelete;

  const toUpdate = zones.toUpdate;
  zones
    .filter((zone) => zone.vertices.updated && zone.id)
    .forEach((zone) => {
      if (toUpdate.indexOf(zone) === -1) {
        toUpdate.push(zone);
      }
    });

  return {
    doorbot_id: doorbotId,
    zone_type: "aerial",
    create: mapZone(toCreate, false),
    delete: mapId(toDelete),
    update: mapZone(toUpdate, true),
  };
};

const canAddZones = (zones) => {
  return zones.length < 3;
};

const addZone = (zones) => {
  const vertices = [
    { x: -5.8884, y: 1.5778 },
    { x: -7.919, y: 4.5721 },
    { x: -4.5721, y: 7.919 },
    { x: 0.0, y: 9.1441 },
    { x: 4.5721, y: 7.919 },
    { x: 7.919, y: 4.5721 },
    { x: 5.8884, y: 1.5778 },
    { x: 0.0, y: 0.0 },
  ];

  const zone = {
    uniqueId: uniqueId("zone-"),
    name: "Bird's Eye Zone " + (zones.length + 1),
    vertices: vertices.map((point) => {
      return { x: point.x, y: point.y };
    }),
    properties: { detection_types: ["motion"] },
    color: hexColors[zones.length % hexColors.length],
  };
  zones.push(zone);

  zone.vertices.forEach((point) => {
    point.zone = zone;
    point.uniqueId = uniqueId("point-");
    point.ui = convertDataPointToScreenPoint(point, zones.options);
  });

  zone.enabled = true;
  zones.forEach((zone) => (zone.selected = false));
  zone.selected = true;

  zone.config = {
    group: konvaGroupConfig(zones.options),
    line: konvaLineConfig(zone, zones.options),
    circles: konvaCirclesConfig(zone, zones.options),
  };
  zone.drawn = true;

  return zone;
};

const removeZone = (zones, selectedZone) => {
  const removedZones = zones.splice(zones.indexOf(selectedZone), 1);

  if (removedZones.length > 0 && removedZones[0].id) {
    zones.toDelete.push(removedZones[0]);
    if (zones.toUpdate.indexOf(selectedZone) !== -1) {
      zones.toUpdate.splice(zones.toUpdate.indexOf(selectedZone), 1);
    }
  }

  const zone = zones.find((zone) => zone.drawn);
  if (zone) zone.selected = true;
};

const updateZone = (zones, selectedZone) => {
  if (selectedZone.id) {
    if (!zones.toUpdate.find((zone) => zone.id === selectedZone.id)) {
      zones.toUpdate.push(selectedZone);
    }
  }
};

const validVertices = (zones) => {
  return !(
    zones.find(
      (zone) =>
        zone.vertices.find(
          (point) =>
            point.x < -10 || point.x > 10 || point.y < 0 || point.y > 10
        ) != null
    ) != null
  );
};

export default {
  buildZonesForUI: (settings, options) => buildZonesForUI(settings, options),
  buildPayloadForSave: (zones, doorbotId) =>
    buildPayloadForSave(zones, doorbotId),
  canAddZones: (zones) => canAddZones(zones),
  addZone: (zones) => addZone(zones),
  removeZone: (zones, selectedZone) => removeZone(zones, selectedZone),
  updateZone: (zones, selectedZone) => updateZone(zones, selectedZone),
  validVertices: (zones) => validVertices(zones),
};
